import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";
import { HeaderMenuItemType } from "./interface";
import {
    findMenuElements,
    findMenuItemElements,
    findMenuItemInEvent,
    findMenuItemWithName,
    findSelectedMenuItemElement,
    getIsFirstChildMenuItem,
    getMenuItemHasChildren,
    getMenuItemPath,
    getMenuItemRoot
} from "./utils";

const baseClass = "uic-header-menu";

/**
 * @slot (default) - the `menu-item` elements belonging to the menu
 */
@Component({
    tag: "uic-header-menu",
    styleUrl: "header-menu.scss",
    shadow: false
})
export class HeaderMenu {
    /** Storage for the closest uic-header element */
    private headerElement: HTMLUicHeaderElement = null;

    @Element() host: HTMLUicHeaderMenuElement;

    /** Name that identifies the menu */
    @Prop({ reflect: true }) readonly name: string;

    /** Name of the currently selected header-menu-item */
    // eslint-disable-next-line
    @Prop({ reflect: true }) current?: string;

    /** Flag that indicates whether the menu behaves as a toggle switch */
    @Prop() readonly toggle?: boolean;

    /** Data provider for the menu child elements */
    @Prop() readonly items: HeaderMenuItemType[] | null = null;

    /** Variant of the header-menu */
    @Prop() readonly variant: "primary" | "secondary" = "primary";

    /** If set to `true` the menu items are arranged vertically */
    @Prop() readonly vertical: boolean = false;

    /** Emitted when a menu item is selected or deselected */
    @Event({ eventName: "uicMenuChange", bubbles: true, cancelable: true })
    menuWillChange: EventEmitter<{
        name: string;
        current: string | null;
    }>;

    /** The currently selected menu-item. */
    @State() selectedItem: HTMLUicHeaderMenuItemElement | null = null;

    @Watch("current")
    setCurrent() {
        this.updateSelectedItems(this.current);
    }

    /** Find uic-header-menu elements with same name */
    private findRelatedMenus = (): HTMLUicHeaderMenuElement[] => {
        return this.headerElement === null ? [this.host] : findMenuElements(this.headerElement, this.name);
    };

    /** Set the focus to the next menu-item on the same level */
    private focusNextItem = (current: HTMLUicHeaderMenuItemElement, dir: number): void => {
        const scope = current.parent || this.headerElement || this.host;
        const items = findMenuItemElements(scope, `[level="${current.level}"]`);
        let index = items.indexOf(current) + dir;
        if (index >= items.length) {
            index = 0;
        } else if (index < 0) {
            index = items.length - 1;
        }
        if (items[index] !== undefined) {
            items[index].focus();
        }
    };

    /** Set the focus to the next menu-item on parent or child level */
    private focusNextLevel = (current: HTMLUicHeaderMenuItemElement, dir: number): void => {
        if (dir > 0) {
            const items = findMenuItemElements(current, `[level="${current.level + 1}"]`);
            if (items.length > 0) {
                setTimeout(() => {
                    items[0].focus();
                });
            }
        } else if (dir < 0 && current.parent !== null) {
            current.parent.focus();
            current.parent.expanded = false;
        }
    };

    /** Internally select or toggle a menu-item */
    private selectItem = (item: HTMLUicHeaderMenuItemElement): void => {
        if (!this.toggle && item === this.selectedItem) {
            return;
        }
        const selected = !this.toggle || !item.selected;
        const selectedItem = selected ? item : null;
        const current = selected ? item.name : null;
        if (
            this.menuWillChange.emit({
                name: this.name,
                current: current
            }).defaultPrevented
        ) {
            return;
        }
        item.selected = selected;
        this.selectedItem = selectedItem;
        this.findRelatedMenus().forEach((el) => {
            el.current = current;
        });
    };

    /** Set the selected property of the menu-items */
    private updateSelectedItems = (current: string): void => {
        const selectedItem = findMenuItemWithName(this.host, current);
        const selectedLevel = selectedItem !== null ? selectedItem.level : -1;
        const selectedPath = getMenuItemPath(selectedItem);
        findMenuItemElements(this.host).forEach((el) => {
            el.selected = typeof current === "string" && el.level <= selectedLevel && el.name === selectedPath[el.level];
            el.expanded = false;
        });
        this.selectedItem = selectedItem;
    };

    /** Click event handler */
    private handleClick = (event) => {
        const item = findMenuItemInEvent(event);
        if (item === null) {
            return;
        }
        if (getMenuItemHasChildren(item)) {
            event.preventDefault();
            event.stopPropagation();
            item.expanded = !item.expanded;
            return;
        }
        this.selectItem(item);
    };

    /** Keyboard event handler */
    private handleKeyDown = (event: KeyboardEvent) => {
        const item = findMenuItemInEvent(event);
        if (item === null) {
            return;
        }
        const hasChildren = getMenuItemHasChildren(item);
        switch (event.key) {
            case "Enter":
            case " ":
                event.preventDefault();
                if (hasChildren) {
                    item.expanded = !item.expanded;
                } else {
                    this.selectItem(item);
                }
                break;
            case "ArrowLeft":
                event.preventDefault();
                if (!this.vertical && item.level === 0) {
                    this.focusNextItem(item, -1);
                } else {
                    if (item.expanded) {
                        item.expanded = false;
                    }
                    this.focusNextLevel(item, -1);
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (this.vertical || item.level > 0) {
                    if (item.level === 1 && getIsFirstChildMenuItem(item)) {
                        this.focusNextLevel(item, -1);
                    } else {
                        this.focusNextItem(item, -1);
                    }
                } else {
                    if (item.expanded) {
                        item.expanded = false;
                    }
                    this.focusNextLevel(item, -1);
                }
                break;
            case "ArrowRight":
                event.preventDefault();
                if (!this.vertical && item.level === 0) {
                    this.focusNextItem(item, 1);
                } else if (hasChildren) {
                    item.expanded = true;
                    this.focusNextLevel(item, 1);
                }
                break;
            case "ArrowDown":
                event.preventDefault();
                if (this.vertical || item.level > 0) {
                    this.focusNextItem(item, 1);
                } else if (hasChildren) {
                    item.expanded = true;
                    this.focusNextLevel(item, 1);
                }
                break;
            default:
                break;
        }
    };

    componentWillLoad() {
        this.headerElement = this.host.closest<HTMLUicHeaderElement>("uic-header");
    }

    componentDidLoad() {
        if (typeof this.current === "string") {
            this.setCurrent();
        } else {
            const selectedItem = findSelectedMenuItemElement(this.host);
            this.selectedItem = selectedItem;
            this.current = selectedItem !== null ? selectedItem.name : null;
        }
    }

    private renderItems() {
        if (this.items === null || this.items.length === 0) {
            return;
        }
        return this.items.map((item) => (
            <uic-header-menu-item key={item.name} name={item.name} label={item.label} icon={item.icon} items={item.items} />
        ));
    }

    render() {
        const { variant, selectedItem, vertical } = this;
        const selectedRootItem = getMenuItemRoot(selectedItem);
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--${variant}`]: variant !== undefined,
                    [`${baseClass}--vertical`]: vertical
                }}
                onClick={this.handleClick}
                onKeyDown={this.handleKeyDown}
            >
                <div
                    class={{
                        [`${baseClass}__inner`]: true
                    }}
                >
                    <slot />
                    {this.renderItems()}
                </div>
                {selectedRootItem !== null && <uic-header-menu-indicator target={selectedRootItem} vertical={vertical} />}
            </Host>
        );
    }
}
